import { Check, Download01, Plus, SearchMd, Trash01, Upload01, XClose } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { pluralize } from "@/utils/text";
import { ImportCsvDialog } from "./import-csv-dialog";
import { TagColorNameInput } from "./tag-color-name-input";
import type { TagLibraryEditor as TagLibraryEditorState } from "./use-tag-library-editor";

/**
 * The tag-library editing UI (controls + table + CSV import), shared by the
 * in-table modal and the Operations page. All state lives in the
 * `useTagLibraryEditor` hook passed in via `editor`; this component is the
 * presentation only. Each container supplies its own Save button / discard guard.
 */
export const TagLibraryEditor = ({ editor }: { editor: TagLibraryEditorState }) => {
  const {
    draft,
    visibleRows,
    search,
    setSearch,
    importOpen,
    setImportOpen,
    confirmingDeleteId,
    scrollContainerRef,
    updateRow,
    addNewTag,
    removeRow,
    confirmDelete,
    cancelDelete,
    handleDownloadTemplate,
  } = editor;

  return (
    <>
      {/* Header controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[14rem] flex-1">
          <Input
            icon={SearchMd}
            placeholder="Search tags"
            inputClassName="text-sm"
            value={search}
            onChange={setSearch}
            onClear={() => setSearch("")}
          />
        </div>
        <Button color="secondary" iconLeading={Plus} onClick={addNewTag}>
          New Tag
        </Button>
        <Button color="tertiary" iconLeading={Download01} onClick={handleDownloadTemplate}>
          Download Template
        </Button>
        <Button color="tertiary" iconLeading={Upload01} onClick={() => setImportOpen(true)}>
          Import CSV
        </Button>
      </div>

      {/* Table */}
      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tag</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead className="text-right">Creators</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-tertiary">
                  {draft.length === 0 ? "No tags yet. Create one to get started." : "No tags match your search."}
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <TagColorNameInput
                        color={row.color}
                        onColorChange={(color) => updateRow(row.id, { color })}
                        name={row.name}
                        onNameChange={(name) => updateRow(row.id, { name })}
                        label="Tag name"
                      />
                    </div>
                  </TableCell>
                  {confirmingDeleteId === row.id ? (
                    // Inline confirm — replaces the old stacked delete dialog. Spans the
                    // remaining columns so the message and actions have room.
                    <TableCell colSpan={4}>
                      <div className="flex items-center justify-end gap-3 whitespace-nowrap">
                        <span className="text-sm text-utility-error-700">
                          Deleting this tag will remove it from {pluralize(row.assignedCount, "creator", "creators")}.
                        </span>
                        <Button
                          color="primary-destructive"
                          size="sm"
                          iconLeading={Check}
                          aria-label="Confirm delete"
                          onClick={() => confirmDelete(row)}
                        />
                        <Button
                          color="tertiary"
                          size="sm"
                          iconLeading={XClose}
                          aria-label="Cancel delete"
                          onClick={cancelDelete}
                        />
                      </div>
                    </TableCell>
                  ) : (
                    <>
                      <TableCell>
                        <span className="text-secondary capitalize">{row.source}</span>
                      </TableCell>
                      <TableCell className="text-secondary">{row.ownerName || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums text-secondary">{row.assignedCount}</TableCell>
                      <TableCell>
                        <Button
                          color="tertiary"
                          size="sm"
                          iconLeading={Trash01}
                          aria-label="Delete tag"
                          onClick={() => removeRow(row)}
                        />
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ImportCsvDialog open={importOpen} onOpenChange={setImportOpen} />
    </>
  );
};
